'use client';
import type { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { ChevronRightIcon } from '@c/icons';
import { useQueryClient } from '@tanstack/react-query';
import { addUserBike } from '@util/firestore/users';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import { isMobile } from '@util/index';
import { logError } from '@util/logError';
import DesktopSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/DesktopSearchByVehicle';
import { FitmentVehicleCard } from 'app/product/[slug]/components/SearchByVehicle/FitmentVehicleCard';
import MobileSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/MobileSearchByVehicle';
import { getFilteredBike } from '@util/hooks/useFitmentFilterSync';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter } from 'context/BikeFilterContext';
import type { UserBike } from 'models/shared';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import InfoIcon from '../icons/info';

export const ShopByDirtBike = ({
  onBikeComplete,
  sideFilters,
  hideYourSavedBikes,
}: {
  onBikeComplete: (bike: UserBike | null) => void;
  sideFilters: CategoryPageSideFilters;
  hideYourSavedBikes?: boolean;
}) => {
  const { userDoc } = useAuth();
  const { selectedBike } = useBikeFilter();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [modalMode, setModalMode] = useState<'filter' | 'add-to-garage'>(
    'filter'
  );
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);
  const isBikeComplete = Boolean(
    selectedBike.type &&
      selectedBike.make &&
      selectedBike.year &&
      selectedBike.model
  );
  async function addBikeToGarage(bike: Partial<UserBike>) {
    if (!userDoc) return;

    try {
      if (
        (userDoc.bikes ?? []).findIndex(
          (b) =>
            b.model === bike.model &&
            b.year === bike.year &&
            b.make === bike.make
        ) === -1
      ) {
        await addUserBike(userDoc.uid, {
          ...(bike as UserBike), // passing bike as UserBike but is is actually a Partial<UserBike>
          date_added: Date.now(),
        });

        onBikeComplete({
          ...(bike as UserBike),
        });
      }
    } catch (e) {
      logError(e);
    }
  }

  return (
    <div className="flex w-full flex-col justify-between gap-y-12 pr-0 sm:mx-auto sm:max-w-content sm:flex-row sm:gap-y-0 sm:pr-6">
      <div className="flex w-full flex-col gap-[1.6rem] sm:flex-row sm:items-center">
        <div className="hidden flex-col gap-1 whitespace-nowrap font-semibold sm:flex">
          <span className="text-[2rem]">Find Parts for Your Ride</span>

          {userDoc && (
            <span className="flex items-center gap-2 whitespace-nowrap text-[1.5rem] text-[#6a6a72]">
              Filter by Year, Make & Model
            </span>
          )}

          {!userDoc && (
            <Link
              href={`/login?redirect=${encodeURIComponent(pathname ?? '')}`}
              className="flex items-center gap-2 whitespace-nowrap text-[1.5rem] text-brand-secondary hover:underline"
            >
              Log In to Add Your Ride
              <ChevronRightIcon className="h-8 w-8" />
            </Link>
          )}
        </div>

        {!userDoc && (
          <VehicleSelector
            fill={false}
            setShowMobileSearchByVehicle={setShowMobileSearchByVehicle}
            setShowDesktopSearchByVehicle={setShowDesktopSearchByVehicle}
            setModalMode={setModalMode}
          />
        )}
      </div>

      {!!userDoc?.bikes?.length && !hideYourSavedBikes && (
        <div className="flex items-center gap-x-2">
          <div className="flex items-center justify-center gap-[1.6rem] sm:min-w-[30rem]">
            <button
              type="button"
              onClick={() => {
                if (isMobile()) {
                  setShowMobileSearchByVehicle(true);
                } else {
                  setShowDesktopSearchByVehicle(true);
                }
              }}
              className="flex w-full items-center justify-center whitespace-nowrap rounded-full bg-black px-24 py-7 text-[1.8rem] font-semibold text-white transition-colors hover:bg-gray-800 sm:w-fit"
            >
              {isBikeComplete ? 'Change vehicle' : 'Select vehicle'}
            </button>
          </div>
        </div>
      )}

      <MobileSearchByVehicle
        show={showMobileSearchByVehicle}
        dismiss={() => setShowMobileSearchByVehicle(false)}
        onBikeComplete={
          modalMode === 'filter' ? onBikeComplete : addBikeToGarage
        }
        onClear={
          modalMode === 'filter' && isBikeComplete
            ? () => onBikeComplete(null)
            : undefined
        }
        variant={modalMode}
        initialBike={modalMode === 'filter' ? selectedBike : null}
        userDoc={userDoc}
      />

      <DesktopSearchByVehicle
        isOpen={showDesktopSearchByVehicle}
        dismiss={() => setShowDesktopSearchByVehicle(false)}
        onBikeComplete={
          modalMode === 'filter' ? onBikeComplete : addBikeToGarage
        }
        onClear={
          modalMode === 'filter' && isBikeComplete
            ? () => onBikeComplete(null)
            : undefined
        }
        variant={modalMode}
        initialBike={modalMode === 'filter' ? selectedBike : null}
      />
    </div>
  );
};

export const VehicleSelector = ({
  fill = true,
  setShowMobileSearchByVehicle,
  setShowDesktopSearchByVehicle,
  setModalMode,
  placeholderText = 'Select vehicle',
}: {
  fill?: boolean;
  setShowMobileSearchByVehicle: (show: boolean) => void;
  setShowDesktopSearchByVehicle: (show: boolean) => void;
  setModalMode?: (mode: 'add-to-garage' | 'filter') => void;
  placeholderText?: string;
}) => {
  const { selectedBike } = useBikeFilter();
  const { userDoc } = useAuth();
  const pathname = usePathname();

  const hasSelectedBike =
    selectedBike.year && selectedBike.make && selectedBike.model;

  return (
    <div className="flex h-full w-full items-center justify-end gap-x-8">
      <button
        type="button"
        onClick={() => {
          if (isMobile()) {
            setModalMode?.('filter');
            setShowMobileSearchByVehicle(true);
          } else {
            setModalMode?.('filter');
            setShowDesktopSearchByVehicle(true);
          }
        }}
        className="flex w-full items-center justify-center whitespace-nowrap rounded-full bg-black px-24 py-7 text-[1.8rem] font-semibold text-white transition-colors hover:bg-gray-800 sm:w-fit"
      >
        <span className="truncate">
          {hasSelectedBike ? 'Change vehicle' : placeholderText}
        </span>
      </button>
    </div>
  );
};

export const ShopByVehicle = ({
  sideFilters,
  onBikeComplete,
  onToggleBikeFilter,
  setShowMobileEditGarageModal,
  prompt,
}: {
  sideFilters: PageSideFilters;
  onBikeComplete: (bike: UserBike | null) => void;
  onToggleBikeFilter?: (enabled: boolean) => void;
  setShowMobileEditGarageModal?: (show: boolean) => void;
  /**
   * Asked in place of the bar's own heading while no vehicle is chosen, for a
   * page that cannot be narrowed until one is.
   */
  prompt?: { title: string; description: string };
}) => {
  const { userDoc } = useAuth();
  const { selectedBike } = useBikeFilter();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);

  // the results are only narrowed down by the vehicle sitting in the filters,
  // so that — and not the remembered vehicle — is what the check reflects
  const filteredBike = getFilteredBike(sideFilters);
  const isFilteringByBike = !!filteredBike;
  const displayedBike = filteredBike ?? selectedBike;

  const hasSelectedBike =
    displayedBike.year && displayedBike.make && displayedBike.model;

  const shouldShowSelectVehicle =
    !userDoc || (userDoc.bikes?.length === 0 && !hasSelectedBike);

  const inGarage = (userDoc?.bikes ?? []).some(
    (bike) =>
      bike.year === displayedBike.year &&
      bike.make === displayedBike.make &&
      bike.model === displayedBike.model
  );

  const saveToGarage = async () => {
    if (!userDoc || !hasSelectedBike) return;
    try {
      await addUserBike(userDoc.uid, {
        ...(displayedBike as UserBike),
        date_added: Date.now(),
      });
    } catch (e) {
      logError(e);
    }
  };

  const toggleBikeFilter = () => {
    if (isFilteringByBike) {
      onToggleBikeFilter?.(false);
      return;
    }
    onBikeComplete(displayedBike as UserBike);
  };

  const asking = !hasSelectedBike && !!prompt;
  const fitmentTitle = asking ? prompt.title : 'Find parts that fit';
  const fitmentDescription = hasSelectedBike
    ? isFilteringByBike
      ? `Showing parts that fit your ${displayedBike.year} ${displayedBike.make} ${displayedBike.model}`
      : `Check the box to only show parts that fit your ${displayedBike.year} ${displayedBike.make} ${displayedBike.model}`
    : asking
    ? prompt.description
    : 'Quickly find parts that fit your vehicle';

  const openSearchByVehicle = () => {
    if (isMobile()) {
      setShowMobileSearchByVehicle(true);
    } else {
      setShowDesktopSearchByVehicle(true);
    }
  };

  return (
    <>
      <div className="hidden w-full bg-[#edededfe] px-8 py-6 sm:block sm:rounded-[1.2rem]">
        <div className="flex w-full items-center justify-between gap-[1.6rem] sm:mx-auto sm:max-w-content">
          <div className="flex flex-col gap-1">
            <span className="text-[2rem] font-semibold text-black">
              {fitmentTitle}
            </span>

            <span className="text-[1.5rem] text-[#6a6a72]">
              {fitmentDescription}
            </span>
          </div>

          <div className="flex items-center gap-x-8">
            {!userDoc && hasSelectedBike && (
              <div className="flex items-center gap-2 whitespace-nowrap">
                <InfoIcon className="h-8 w-8 shrink-0 text-brand-secondary" />

                <span className="text-[1.5rem] font-medium text-zinc-800">
                  <Link
                    href="/signup"
                    className="font-semibold text-brand-secondary underline"
                  >
                    Sign up
                  </Link>{' '}
                  to save your vehicle info
                </span>
              </div>
            )}

            {hasSelectedBike ? (
              <FitmentVehicleCard
                variant="trigger"
                bike={displayedBike}
                isSelected={isFilteringByBike}
                onClick={openSearchByVehicle}
                onDeselect={() => onBikeComplete(null)}
                onToggleSelected={toggleBikeFilter}
                onSave={userDoc && !inGarage ? saveToGarage : undefined}
                className="w-fit min-w-[30rem]"
              />
            ) : (
              <button
                type="button"
                onClick={openSearchByVehicle}
                className="flex items-center justify-center whitespace-nowrap rounded-full bg-black px-16 py-5 text-[1.6rem] font-semibold text-white transition-colors hover:bg-gray-800"
              >
                Select vehicle
              </button>
            )}
          </div>
        </div>
      </div>

      {/* mobile view of shop by vehicle */}
      <div className="mb-2 w-full sm:mb-8 sm:hidden">
        <div className="flex w-full flex-col items-start justify-between space-y-10 bg-[#F6F6F6] p-[2rem]">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-2">
              <span className="text-[2.2rem] font-semibold text-black">
                {fitmentTitle}
              </span>

              {(!userDoc || hasSelectedBike || asking) && (
                <p className="text-[1.6rem] font-medium text-zinc-700">
                  {fitmentDescription}
                </p>
              )}
            </div>

            {userDoc && !!userDoc.bikes?.length && (
              <button
                type="button"
                className="text-[1.8rem] font-semibold text-brand-secondary"
                onClick={() => {
                  setShowMobileSearchByVehicle(true);
                }}
              >
                Edit
              </button>
            )}
          </div>

          {!hasSelectedBike ? (
            <>
              {shouldShowSelectVehicle && (
                <button
                  type="button"
                  className="h-[7rem] w-full rounded-[2rem] bg-brand-secondary text-[2.1rem] font-semibold text-white"
                  onClick={() => {
                    setShowMobileSearchByVehicle(true);
                  }}
                >
                  Select a vehicle
                </button>
              )}
            </>
          ) : (
            <>
              {!userDoc && (
                <div className="flex w-full flex-col space-y-12">
                  <FitmentVehicleCard
                    variant="trigger"
                    bike={displayedBike}
                    isSelected={isFilteringByBike}
                    onClick={() => setShowMobileSearchByVehicle(true)}
                    onDeselect={() => onBikeComplete(null)}
                    onToggleSelected={toggleBikeFilter}
                  />

                  <div className="flex items-center gap-2">
                    <InfoIcon className="h-10 w-10 text-brand-secondary" />

                    <span className="text-[1.6rem] font-medium text-zinc-800">
                      <Link href="/signup" className="font-bold underline">
                        Sign up
                      </Link>{' '}
                      to save your vehicle info
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <MobileSearchByVehicle
        show={showMobileSearchByVehicle}
        dismiss={() => setShowMobileSearchByVehicle(false)}
        onBikeComplete={onBikeComplete}
        onClear={hasSelectedBike ? () => onBikeComplete(null) : undefined}
        initialBike={hasSelectedBike ? displayedBike : null}
        userDoc={userDoc}
      />

      <DesktopSearchByVehicle
        isOpen={showDesktopSearchByVehicle}
        dismiss={() => setShowDesktopSearchByVehicle(false)}
        onBikeComplete={onBikeComplete}
        onClear={hasSelectedBike ? () => onBikeComplete(null) : undefined}
        initialBike={hasSelectedBike ? displayedBike : null}
      />
    </>
  );
};
